import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  App,
  Form,
  Input,
  Button,
  Table,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Card,
  Row,
  Col,
  Dropdown,
  Typography,
  theme,
  DatePicker,
} from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import { PlusOutlined, EllipsisOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import PageHeader from '../../components/common/PageHeader';
import {
  listarOcorrencias,
  criarOcorrencia,
  atualizarOcorrencia,
  deletarOcorrencia,
  type Ocorrencia,
} from '../../services/ocorrenciasService';

const { Text } = Typography;

const DATE_FORMAT = 'DD/MM/YYYY HH:mm';

// Toolbar completa do Quill com opções de fonte, tamanho, cores e formatações
const QUILL_MODULES = {
  toolbar: [
    [{ font: [] }, { size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
};

/** Remove tags HTML para exibição de texto puro na tabela */
const stripHtml = (html: string): string => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

/** Trunca o texto em 20 caracteres, adicionando reticências se necessário */
const truncar = (text: string): string =>
  text.length > 20 ? `${text.slice(0, 20)}…` : text;

/** Formata uma data ISO para o padrão dd/mm/aaaa hh:mm ou retorna "-" */
const formatarData = (data: string | null): string => {
  if (!data) return '-';
  return dayjs(data).format(DATE_FORMAT);
};

type StatusOcorrencia = 'Deletada' | 'Resolvida' | 'Em andamento';

/** Define o status da ocorrência com base no término e exclusão lógica */
const resolverStatus = (termino: string | null, deletedAt: string | null): StatusOcorrencia => {
  if (deletedAt) return 'Deletada';
  if (termino) return 'Resolvida';
  return 'Em andamento';
};

const STATUS_TAG_COLOR: Record<StatusOcorrencia, string> = {
  Deletada: 'error',
  Resolvida: 'success',
  'Em andamento': 'processing',
};

interface FormValues {
  inicio: Dayjs;
  termino?: Dayjs | null;
  titulo: string;
  descricao: string;
}

const OcorrenciasContent: React.FC = () => {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [ocorrencias, setOcorrencias] = useState<Ocorrencia[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // ID da ocorrência em edição (null indica modo de criação)
  const [editingId, setEditingId] = useState<number | null>(null);
  // Indica que o formulário está em modo somente leitura (ocorrência deletada)
  const [viewingOnly, setViewingOnly] = useState(false);
  // Valor atual do editor Quill (controlado separadamente por limitação do componente)
  const [descricaoValue, setDescricaoValue] = useState('');
  // Mensagem de erro do campo descrição (mantida em estado para garantir re-render)
  const [descricaoError, setDescricaoError] = useState('');
  const formRef = useRef<HTMLDivElement>(null);

  const isEditing = editingId !== null;

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarOcorrencias();
      setOcorrencias(data);
    } catch {
      message.error('Erro ao carregar ocorrências');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const handleDescricaoChange = (value: string) => {
    setDescricaoValue(value);
    form.setFieldValue('descricao', value);
    // Limpa o erro ao digitar caso já exista
    if (stripHtml(value).trim()) {
      setDescricaoError('');
    }
  };

  /**
   * Disparado quando Ant Design detecta erros nos campos registrados (inicio, titulo).
   * Garante que o erro da descrição também apareça simultaneamente.
   */
  const handleFinishFailed = () => {
    if (!stripHtml(descricaoValue).trim()) {
      setDescricaoError('Informe a descrição');
    }
  };

  const handleSalvar = async (values: FormValues) => {
    // Valida que a descrição não está vazia (Quill retorna '<p><br></p>' quando vazio)
    const textoPlano = stripHtml(descricaoValue).trim();
    if (!textoPlano) {
      setDescricaoError('Informe a descrição');
      return;
    }
    setDescricaoError('');

    setSaving(true);
    try {
      const inicioIso  = values.inicio.toISOString();
      const terminoIso = values.termino ? values.termino.toISOString() : null;

      if (isEditing) {
        await atualizarOcorrencia(editingId, {
          termino:   terminoIso,
          descricao: descricaoValue,
        });
        message.success('Ocorrência atualizada com sucesso');
      } else {
        await criarOcorrencia({
          inicio:    inicioIso,
          termino:   terminoIso,
          titulo:    values.titulo,
          descricao: descricaoValue,
        });
        message.success('Ocorrência criada com sucesso');
      }

      form.resetFields();
      setDescricaoValue('');
      setEditingId(null);
      await carregar();
    } catch {
      message.error('Erro ao salvar ocorrência');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelar = () => {
    form.resetFields();
    setDescricaoValue('');
    setDescricaoError('');
    setEditingId(null);
    setViewingOnly(false);
  };

  const handleVisualizar = (record: Ocorrencia) => {
    setEditingId(record.id);
    setViewingOnly(true);
    setDescricaoValue(record.descricao);
    setDescricaoError('');
    form.setFieldsValue({
      inicio:    dayjs(record.inicio),
      termino:   record.termino ? dayjs(record.termino) : null,
      titulo:    record.titulo,
      descricao: record.descricao,
    });
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleEditar = (record: Ocorrencia) => {
    setEditingId(record.id);
    setViewingOnly(false);
    setDescricaoValue(record.descricao);
    setDescricaoError('');
    form.setFieldsValue({
      inicio:    dayjs(record.inicio),
      termino:   record.termino ? dayjs(record.termino) : null,
      titulo:    record.titulo,
      descricao: record.descricao,
    });
    // Rola até o formulário para que o usuário possa editá-lo
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDeletar = async (id: number) => {
    try {
      await deletarOcorrencia(id);
      message.success('Ocorrência deletada com sucesso');
      await carregar();
    } catch {
      message.error('Erro ao deletar ocorrência');
    }
  };

  /** Monta o menu de ações (3 pontinhos) de cada linha da tabela */
  const getRowMenuItems = (record: Ocorrencia): MenuProps['items'] => {
    // Ocorrências deletadas só permitem visualização
    if (record.deletedAt) {
      return [
        {
          key: 'visualizar',
          label: 'Visualizar',
          onClick: () => handleVisualizar(record),
        },
      ];
    }

    return [
      {
        key: 'editar',
        label: 'Editar',
        onClick: () => handleEditar(record),
      },
      {
        key: 'deletar',
        label: (
          <Popconfirm
            title="Deletar ocorrência"
            description="Tem certeza que deseja deletar esta ocorrência?"
            onConfirm={() => handleDeletar(record.id)}
            okText="Sim, deletar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Text style={{ color: token.colorError }}>Deletar</Text>
          </Popconfirm>
        ),
      },
    ];
  };

  const columns: TableColumnsType<Ocorrencia> = [
    { title: 'ID', dataIndex: 'id', width: 65 },
    {
      title: 'Título',
      dataIndex: 'titulo',
      render: (v: string) => (
        <Tooltip title={v}>
          <span>{truncar(v)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      render: (v: string) => {
        const plain = stripHtml(v);
        return (
          <Tooltip
            title={
              <div
                style={{ whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: v }}
              />
            }
            styles={{ body: { maxWidth: 600, width: 600 }, root: { maxWidth: 640 } }}
          >
            <span style={{ display: 'inline-block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
              {truncar(plain)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Início',
      dataIndex: 'inicio',
      render: (v: string) => formatarData(v),
    },
    {
      title: 'Término',
      dataIndex: 'termino',
      render: (v: string | null) => formatarData(v),
    },
    {
      title: 'Criado em',
      dataIndex: 'createdAt',
      render: (v: string) => formatarData(v),
    },
    {
      title: 'Atualizado em',
      dataIndex: 'updatedAt',
      render: (v: string | null) => (v ? formatarData(v) : '—'),
    },
    {
      title: 'Deletado em',
      dataIndex: 'deletedAt',
      render: (v: string | null) => formatarData(v),
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      render: (_: unknown, record: Ocorrencia) => {
        const status = resolverStatus(record.termino, record.deletedAt);
        return (
          <Tag color={STATUS_TAG_COLOR[status]}>
            {status.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: '',
      key: 'acoes',
      width: 48,
      fixed: 'right' as const,
      render: (_: unknown, record: Ocorrencia) => (
        <Dropdown menu={{ items: getRowMenuItems(record) }} trigger={['click']}>
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <PageHeader
        title="Status Ocorrências"
        subtitle="Gerencie as ocorrências do sistema de status"
      />

      {/* Formulário de criação / edição / visualização */}
      <div ref={formRef}>
        <Card
          title={
            viewingOnly
              ? `Visualizar Ocorrência #${editingId}`
              : isEditing
              ? `Editar Ocorrência #${editingId}`
              : 'Nova Ocorrência'
          }
          style={{ borderRadius: token.borderRadiusLG }}
        >
          <Form form={form} layout="vertical" onFinish={handleSalvar} onFinishFailed={handleFinishFailed}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="inicio"
                  label="Início"
                  rules={[{ required: true, message: 'Informe a data de início' }]}
                >
                  <DatePicker
                    format={DATE_FORMAT}
                    showTime={{ format: 'HH:mm' }}
                    placeholder="dd/mm/aaaa hh:mm"
                    style={{ width: '100%' }}
                    disabled={isEditing || viewingOnly}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="termino"
                  label="Término"
                  dependencies={['inicio']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value) return Promise.resolve();
                        if (!getFieldValue('inicio')) {
                          return Promise.reject(
                            new Error('Informe o início antes de definir o término'),
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    format={DATE_FORMAT}
                    showTime={{ format: 'HH:mm' }}
                    placeholder="Indefinido"
                    style={{ width: '100%' }}
                    disabled={viewingOnly}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="titulo"
              label="Título"
              rules={[
                { required: true, message: 'Informe o título' },
                { max: 100, message: 'Máximo de 100 caracteres' },
              ]}
            >
              <Input
                maxLength={100}
                showCount
                placeholder="Título da ocorrência"
                disabled={isEditing || viewingOnly}
              />
            </Form.Item>

            {/* Campo descricao registrado no Form apenas para validação;
                o valor real é controlado via estado descricaoValue */}
            <Form.Item name="descricao" hidden>
              <Input />
            </Form.Item>

            <Form.Item
              label="Descrição"
              required
              validateStatus={descricaoError ? 'error' : ''}
              help={descricaoError || undefined}
            >
              <ReactQuill
                theme="snow"
                modules={QUILL_MODULES}
                value={descricaoValue}
                onChange={handleDescricaoChange}
                placeholder="Descreva a ocorrência..."
                style={{ minHeight: 300 }}
                readOnly={viewingOnly}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
              <Space>
                <Button type="primary" htmlType="submit" loading={saving} disabled={viewingOnly}>
                  Salvar
                </Button>
                <Button onClick={handleCancelar} disabled={viewingOnly}>Cancelar</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </div>

      {/* Tabela de ocorrências */}
      <Card
        title="Ocorrências Registradas"
        extra={
          isEditing ? (
            <Button icon={<PlusOutlined />} onClick={handleCancelar}>
              Nova Ocorrência
            </Button>
          ) : null
        }
        style={{ borderRadius: token.borderRadiusLG }}
      >
        <Table
          columns={columns}
          dataSource={ocorrencias}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          onRow={() => ({
            onMouseEnter: (e) => {
              (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                token.colorBgTextHover;
            },
            onMouseLeave: (e) => {
              (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '';
            },
          })}
          scroll={{ x: 1000 }}
          size="middle"
        />
      </Card>
    </Space>
  );
};

const Ocorrencias: React.FC = () => (
  <OcorrenciasContent />
);

export default Ocorrencias;
